import { OneClickFollowBotData, OpenMessage } from "@shared/types";
import { useContext, useMemo, useRef } from "react";
import { sortByStringProperty } from "@shared/utils/general";
import { getSignalFollowType } from "@shared/features/signals/common/utils";
import { FollowSignalsContext } from "@shared/features/signals/common/FollowSignals.context";
import { checkSearch } from "@shared/utils/misc";
import { partition } from "lodash";

interface useSignalFollowBotsListProps {
  onListItemSelected: (botData?: OneClickFollowBotData) => void;
  openMessage: OpenMessage;
  searchValue?: string;
}

const useSignalFollowBotsList = ({
  onListItemSelected,
  openMessage,
  searchValue,
}: useSignalFollowBotsListProps) => {
  const {
    getBotsOneClickDataStatus,
    getItemErrorMessage,
    signal,
    getBotAccountData,
  } = useContext(FollowSignalsContext)!;
  const isEmptyBots = !getBotsOneClickDataStatus.data?.length;
  const botsData = getBotsOneClickDataStatus.data?.filter(
    (item) => item.sub_group_id === signal?.sub_group_id
  );
  const onListItemSelectedRef = useRef<any>(undefined);
  onListItemSelectedRef.current = onListItemSelected;

  const getBotData = (bot: OneClickFollowBotData) => {
    const error = getItemErrorMessage(undefined, bot);
    return {
      ...bot,
      error,
      onClick: () => {
        if (error) {
          openMessage(error, "warning");
        } else {
          onListItemSelectedRef.current?.(bot);
        }
      },
    };
  };

  const filteredBots = useMemo(() => {
    return searchValue
      ? botsData?.filter((botData) => checkSearch(botData.name, searchValue))
      : botsData;
  }, [searchValue, botsData]);

  const sortedBots = useMemo(() => {
    const validBots: FollowSignalBotData[] = [];
    const followingBots: FollowSignalBotData[] = [];
    const invalidBots: FollowSignalBotData[] = [];
    filteredBots
      ?.slice()
      .sort((a, b) => sortByStringProperty(a, b, "name"))
      .forEach((botData) => {
        const parsedData = getBotData(botData);
        const followType = getSignalFollowType(signal, undefined, botData.id);
        if (followType) followingBots.push(parsedData);
        else if (parsedData.error) invalidBots.push(parsedData);
        else validBots.push(parsedData);
      });
    return [...validBots, ...followingBots, ...invalidBots];
  }, [filteredBots]);

  const [groupBots, otherBots] = useMemo(() => {
    return partition(
      sortedBots,
      (bot) => bot.sub_group_id === signal?.sub_group_id
    );
  }, [sortedBots, signal]);

  const unfilteredGroupBots = useMemo(() => {
    return botsData?.filter((bot) => bot.sub_group_id === signal?.sub_group_id);
  }, [botsData, signal]);

  const botsGroupsData: {
    title: string;
    bots: FollowSignalBotData[];
    emptyText?: string;
    isEmpty?: boolean;
    showEmptyButton?: boolean;
  }[] = [
    {
      bots: groupBots,
      title: "Current Group Bots",
      emptyText: unfilteredGroupBots?.length
        ? "None of the selected signal's group bots match your search query."
        : "You currently don't have any bots that are connected to the selected signal's group.",
      isEmpty: !groupBots.length,
      showEmptyButton: !unfilteredGroupBots?.length,
    },
  ];
  if (otherBots.length) {
    botsGroupsData.push({
      bots: otherBots,
      title: "Other Groups Bots",
    });
  }

  return {
    botsGroupsData,
    getBotAccountData,
    signal,
    isEmptyBots,
  };
};

export default useSignalFollowBotsList;

export type FollowSignalBotData = OneClickFollowBotData & {
  error?: string;
  onClick?: () => void;
};
